'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useId, useRef, useState } from 'react'
import { BrandedLoader } from '@/components/ui/branded-loader'
import { Button } from '@/components/ui/button'
import { isLoginPath, useNativeSessionBackup } from '@/hooks/use-native-session-backup'
import type { BiometricKind } from '@/lib/native/session-backup'
import { isNativePlatform } from '@/lib/utils/platform'

// These two surfaces carry their own `data-ui="encuentro"`: they mount at the
// root of the shell, in the classic UI too, and the Alumnia UI tokens only
// exist under that attribute (lib/design/encuentro-scope.ts).
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled])'

const METHOD_KEY = {
  faceId: 'methodFaceId',
  touchId: 'methodTouchId',
  fingerprint: 'methodFingerprint',
  face: 'methodFace',
  iris: 'methodGeneric',
  generic: 'methodGeneric',
} as const satisfies Record<BiometricKind, string>

/**
 * Native session re-entry UI (phase 2). Mounted once in `NativeAppShell`.
 * Renders nothing on web and nothing while there is nothing to do:
 *  - a branded cover while the session is rebuilt from the secure backup,
 *  - the Face ID / fingerprint lock when the person turned it on,
 *  - a one-time card offering "Entrar con Face ID la próxima vez".
 */
export function NativeSessionBackup() {
  const t = useTranslations('common.sessionBackup')
  const pathname = usePathname()
  const { gate, offer, unlockFailed, unlock, skip, acceptOffer, declineOffer } =
    useNativeSessionBackup(t('lockReason'))
  const autoPrompted = useRef(false)
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const lockRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const bodyId = useId()

  // Ask for the biometric as soon as the lock appears on a signed-in screen.
  useEffect(() => {
    if (gate !== 'locked') {
      autoPrompted.current = false
      setConfirmSignOut(false)
      setSigningOut(false)
      return
    }
    if (autoPrompted.current || isLoginPath(pathname)) return
    autoPrompted.current = true
    void unlock()
  }, [gate, pathname, unlock])

  // The lock is a modal: focus goes in, stays in (Tab wraps), and follows the
  // step (lock, then the sign-out question) so a keyboard or screen reader
  // never ends up on the app behind it.
  const locked = isNativePlatform() && gate === 'locked'
  useEffect(() => {
    if (!locked) return
    const root = lockRef.current
    root?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
  }, [locked, confirmSignOut, unlockFailed])

  if (!isNativePlatform()) return null

  if (gate === 'restoring') {
    return (
      <div className="fixed inset-0 z-10000" data-testid="session-restoring">
        <BrandedLoader message={null} />
      </div>
    )
  }

  if (gate === 'locked') {
    const onLogin = isLoginPath(pathname)
    const otherMethod = () => {
      if (onLogin) {
        skip()
        return
      }
      // Signing out deletes the secure backup: ask first.
      setConfirmSignOut(true)
    }
    const signOut = async () => {
      setSigningOut(true)
      try {
        const { performSignOut } = await import('@/lib/utils/sign-out')
        await performSignOut()
      } finally {
        setSigningOut(false)
      }
    }
    const trapTab = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Tab') return
      const nodes = lockRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!nodes || nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || !lockRef.current?.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !lockRef.current?.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }
    return (
      <div
        ref={lockRef}
        data-ui="encuentro"
        data-testid="session-lock"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={trapTab}
        className="fixed inset-0 z-10000 grid place-items-center bg-en-canvas p-6 font-en-sans text-en-ink pt-safe pb-safe"
      >
        <div className="grid w-full max-w-sm grid-cols-[minmax(0,1fr)] justify-items-center gap-4 rounded-en-lg bg-en-surface p-6 text-center shadow-en-overlay">
          <Image
            src="/brand/encuentro/lockup-horizontal-blue.svg"
            alt=""
            width={124}
            height={32}
            unoptimized
            className="h-8 w-auto"
          />
          {confirmSignOut ? (
            <>
              <h2 id={titleId} className="text-en-subheading">
                {t('signOutTitle')}
              </h2>
              <p id={bodyId} className="text-en-body text-en-ink-muted">
                {t('signOutBody')}
              </p>
              <div className="grid w-full gap-2">
                <Button
                  look="encuentro"
                  variant="danger"
                  block
                  disabled={signingOut}
                  onClick={() => void signOut()}
                >
                  {signingOut ? t('signingOut') : t('signOutConfirm')}
                </Button>
                <Button
                  look="encuentro"
                  variant="ghost"
                  block
                  disabled={signingOut}
                  onClick={() => setConfirmSignOut(false)}
                >
                  {t('signOutCancel')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 id={titleId} className="text-en-subheading">
                {t('lockTitle')}
              </h2>
              <p
                id={bodyId}
                role={unlockFailed ? 'alert' : undefined}
                className="text-en-body text-en-ink-muted"
              >
                {unlockFailed ? t('lockFailed') : t('lockDescription')}
              </p>
              <div className="grid w-full gap-2">
                <Button look="encuentro" variant="primary" block onClick={() => void unlock()}>
                  {unlockFailed ? t('retry') : t('unlock')}
                </Button>
                <Button look="encuentro" variant="ghost" block onClick={otherMethod}>
                  {t('otherMethod')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  if (offer) {
    const method = t(METHOD_KEY[offer.kind])
    return (
      <div
        data-ui="encuentro"
        data-testid="session-offer"
        role="dialog"
        aria-labelledby={`${titleId}-offer`}
        className="fixed inset-x-0 bottom-0 z-9000 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] font-en-sans text-en-ink"
      >
        <div className="mx-auto grid w-full max-w-md gap-4 rounded-en-lg bg-en-surface p-4 shadow-en-overlay">
          <div className="grid gap-1">
            <h2 id={`${titleId}-offer`} className="text-en-subheading">
              {t('offerTitle', { method })}
            </h2>
            <p className="text-en-body text-en-ink-muted">{t('offerDescription')}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button look="encuentro" variant="ghost" onClick={() => void declineOffer()}>
              {t('offerDecline')}
            </Button>
            <Button
              look="encuentro"
              variant="primary"
              onClick={() => void acceptOffer(t('lockReason'))}
            >
              {t('offerAccept')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
